import { useMemo } from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import PermissionCollapse from "../PermissionCollapse/PermissionCollapse";
import { getPermissionGroupsForForm } from "../../../../../utils/constants/permissionsConstants";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { HIDDEN_PERMISSION_KEYS, DEV_ONLY_PERMISSION_KEYS } from "../../constants";
import type { PermissionCategory, PermissionState } from "../../types";
import styles from "./PermissionsSection.module.css";

interface PermissionsSectionProps {
  categories: PermissionCategory[];
  permissions: PermissionState;
  onPermissionsChange: (updated: PermissionState) => void;
  disabled?: boolean;
  filterKeys?: string[];
  devOnlyKeys?: string[];
}

// Convert old-style permission groups into PermissionCategory format
function buildFallbackCategories(): PermissionCategory[] {
  const groups = getPermissionGroupsForForm();
  return groups.map(
    (group: { id: string; label: string; children: { id: string; label: string }[] }) => ({
      category_key: group.id,
      display_name: group.label,
      items: group.children.map((child: { id: string; label: string }) => ({
        key: child.id,
        display_name: child.label,
      })),
    }),
  );
}

function PermissionsSection({
  categories,
  permissions,
  onPermissionsChange,
  disabled = false,
  filterKeys = HIDDEN_PERMISSION_KEYS,
  devOnlyKeys = DEV_ONLY_PERMISSION_KEYS,
}: PermissionsSectionProps) {
  const effectiveCategories = useMemo(
    () => (categories?.length ? categories : buildFallbackCategories()),
    [categories],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>{AppStrings.PERMISSIONS}</span>
        <Tooltip
          title="Toggle permissions to control what sections and pages this role can access"
          placement="top"
          color="var(--COLOR-PRIMARY_BACKGROUND_COLOR)"
        >
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </div>
      <PermissionCollapse
        categories={effectiveCategories}
        permissions={permissions}
        onPermissionsChange={onPermissionsChange}
        disabled={disabled}
        filterKeys={filterKeys}
        devOnlyKeys={devOnlyKeys}
      />
    </div>
  );
}

export default PermissionsSection;
